import {
  BillingInvoiceDto,
  BillingPlanDto,
  BillingPlanListType,
  BillingSubscriptionDto,
  ChangePaddleSubscriptionPlanBody,
  UpdateBillingSubscriptionPreviewDto,
} from '../core/billing';
import type { HttpClient } from './http';
import { catch404ReturnNull } from './util';

export interface ShopifyAdminUrlDto {
  shopify_admin_url: string;
}

export type StartShopifyCheckoutParams = {
  paddle_plan_id: number;
  is_yearly?: boolean;
};

export interface ShopifyBillingChargePlanDto {
  key: string;
  displayName: string;
  amount: number;
  currencyCode: 'USD';
  interval: 'EVERY_30_DAYS' | 'ANNUAL';
  trialDays?: number;
}

export interface BillingService {
  getSubscription: () => Promise<BillingSubscriptionDto | null>;
  getFreePlan: () => Promise<BillingPlanDto>;
  cancelSubscription: () => Promise<void>;
  changeSubscription: (
    params: ChangePaddleSubscriptionPlanBody,
  ) => Promise<void>;
  getUpdateSubscriptionPreview: (
    planId: number,
  ) => Promise<UpdateBillingSubscriptionPreviewDto>;
  getSubscriptionPlans: (
    params: GetSubscriptionPlansDto,
  ) => Promise<BillingPlanDto[]>;
  getInvoices: () => Promise<BillingInvoiceDto[]>;
  /**
   * Returns the Shopify Admin embedded-app URL the merchant must visit to
   * approve the chosen plan via `appSubscriptionCreate`. Only callable by
   * Shopify-billed users.
   *
   * `isYearly` disambiguates plans that share a `paddle_id` but map to
   * separate monthly/yearly Shopify billing entries.
   */
  startShopifyCheckout: (
    params: StartShopifyCheckoutParams,
  ) => Promise<ShopifyAdminUrlDto>;
  /** Returns the Shopify Admin URL for managing the current subscription. */
  getShopifyManageUrl: () => Promise<ShopifyAdminUrlDto>;
  /** Deep link into the embedded app with `intent=cancel` to run app-side cancellation. */
  getShopifyCancelUrl: () => Promise<ShopifyAdminUrlDto>;
}

export type GetSubscriptionPlansDto = {
  type?: BillingPlanListType;
};

export function createBillingService(
  httpClient: HttpClient,
  baseUrl: string,
): BillingService {
  const BASE_URL = `${baseUrl}/billing`;

  return {
    getSubscription: (): Promise<BillingSubscriptionDto | null> => {
      return catch404ReturnNull(() =>
        httpClient<BillingSubscriptionDto>(`${BASE_URL}/subscription`, 'get'),
      );
    },

    getFreePlan: (): Promise<BillingPlanDto> => {
      return httpClient<BillingPlanDto>(`${BASE_URL}/plans/free`, 'get');
    },

    cancelSubscription: (): Promise<void> => {
      return httpClient<void>(`${BASE_URL}/subscription`, 'delete');
    },

    changeSubscription: (
      params: ChangePaddleSubscriptionPlanBody,
    ): Promise<void> => {
      return httpClient<void>(`${BASE_URL}/subscription`, 'patch', {
        body: params,
      });
    },

    getUpdateSubscriptionPreview: (
      planId: number,
    ): Promise<UpdateBillingSubscriptionPreviewDto> => {
      return httpClient<UpdateBillingSubscriptionPreviewDto>(
        `${BASE_URL}/subscription/update-preview/${planId}`,
        'get',
      );
    },

    getSubscriptionPlans: (
      params: GetSubscriptionPlansDto,
    ): Promise<BillingPlanDto[]> => {
      return httpClient<BillingPlanDto[]>(
        `${BASE_URL}/plans/${params.type ?? ''}`,
        'get',
      );
    },

    getInvoices: (): Promise<BillingInvoiceDto[]> => {
      return httpClient<BillingInvoiceDto[]>(`${BASE_URL}/invoices`, 'get');
    },

    startShopifyCheckout: (
      params: StartShopifyCheckoutParams,
    ): Promise<ShopifyAdminUrlDto> => {
      return httpClient<ShopifyAdminUrlDto>(
        `${BASE_URL}/shopify/start-checkout`,
        'post',
        { body: params },
      );
    },

    getShopifyManageUrl: (): Promise<ShopifyAdminUrlDto> => {
      return httpClient<ShopifyAdminUrlDto>(
        `${BASE_URL}/shopify/manage-url`,
        'get',
      );
    },

    getShopifyCancelUrl: (): Promise<ShopifyAdminUrlDto> => {
      return httpClient<ShopifyAdminUrlDto>(
        `${BASE_URL}/shopify/cancel-url`,
        'get',
      );
    },
  };
}
